import React from 'react';
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import createCache from "@emotion/cache";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { Routes, Route } from "react-router-dom";
import URIs from './utils/uri';
import { PathSelectedProvider } from './context/PathSelectedProvider';
import { NotificationProvider } from './context/NotificationProvider';
import PersistLogin from './logics/PersistLogin';
import RequireAuth from './logics/RequireAuth';
import DefaultLayout from './layouts/DefaultLayout';
import AltLayout from './layouts/AltLayout';
import AdminLayout from './layouts/AdminLayout';
import SignInLayout from './layouts/SignInLayout';
import Home from "./pages/Home";
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Verify from './pages/auth/Verify';
import SetPassword from './pages/auth/SetPassword';
import Kyc from './pages/auth/Kyc';
import Missing from './pages/Missing';
import Unauthorized from './pages/Unauthorized';
import Goldbox from './pages/Goldbox';
import Transactions from './pages/Transactions';
import Wallet from './pages/Wallet';
import Menu from './pages/menu/Menu';
import Order from './pages/order/Order';

// admin routes
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminGoldBox from './pages/admin/AdminGoldBox';
import AdminMenu from './pages/admin/AdminMenu';

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin]
});

function App() {
  const [isRtl] = React.useState(true);

  React.useLayoutEffect(() => {
    document.body.setAttribute("dir", isRtl ? "rtl" : "ltr");
  }, [isRtl]);

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <PathSelectedProvider>
          <CssBaseline />
          <NotificationProvider>
            <Routes>
              <Route element={<SignInLayout />}>
                <Route path={URIs.LOGIN} element={<Login />} />
                <Route path={URIs.REGISTER} element={<Register />} />
                <Route path={URIs.VERIFY} element={<Verify />} />
                <Route path={URIs.SET_PASSWORD} element={<SetPassword />} />
                <Route path={URIs.KYC} element={<Kyc />} />
              </Route>
              <Route element={<PersistLogin />}>
                <Route element={<RequireAuth />}>
                  <Route path="/" element={<DefaultLayout />}>
                    <Route index  element={<Home />} />
                    <Route path={URIs.GOLD_BOX} element={<Goldbox />} />
                    <Route path={URIs.TRANSACTOINS} element={<Transactions />} />
                    <Route path={URIs.WALLET} element={<Wallet />} />
                    <Route path={URIs.MENU} element={<Menu />} />
                  </Route>
                  <Route element={<AltLayout />}>
                    <Route path={URIs.ORDER} element={<Order />} />
                  </Route>
                </Route>

                <Route element={<RequireAuth allowedRoles={[2, 3]} />}>
                  <Route path={URIs.ADMIN.ROOT} element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path={URIs.ADMIN.ORDERS} element={<AdminOrders />} />
                    <Route path={URIs.ADMIN.GOLD_BOX} element={<AdminGoldBox />} />
                    <Route path={URIs.ADMIN.USERS} element={<AdminUsers />} />
                    <Route path={URIs.ADMIN.MENU} element={<AdminMenu />} />
                  </Route>
                </Route>
              </Route>
              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route path="*" element={<Missing />} />
            </Routes>
          </NotificationProvider>
        </PathSelectedProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
